import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Box } from '@mui/material';
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { BackToDashboardLink } from '@/components/common';
import type { NavItem } from '@/components/layout/NavItem';
import { PLATFORM_TIERS, ROLES } from '@/constants/roles';
import { ROUTES } from '@/constants/routes';
import { MyInvitationsPage } from '@/features/company-portal/pages/MyInvitationsPage';
import { CompanyGuard } from '@/features/company-portal/workspace/CompanyGuard';
import { RegisterCompanyPage } from '@/features/company-registration/pages/RegisterCompanyPage';
import { VerifyCompanyRegistrationPage } from '@/features/company-registration/pages/VerifyCompanyRegistrationPage';
import { ChangePasswordPage } from '@/features/identity/pages/ChangePasswordPage';
import { ForgotMfaPage } from '@/features/identity/pages/ForgotMfaPage';
import { ForgotPasswordPage } from '@/features/identity/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/identity/pages/LoginPage';
import { MfaChallengePage } from '@/features/identity/pages/MfaChallengePage';
import { MfaSetupPage } from '@/features/identity/pages/MfaSetupPage';
import { ProfilePage } from '@/features/identity/pages/ProfilePage';
import { RegisterPage } from '@/features/identity/pages/RegisterPage';
import { ResetPasswordPage } from '@/features/identity/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/features/identity/pages/VerifyEmailPage';
import { NotificationHistoryPage } from '@/features/notifications/pages/NotificationHistoryPage';
import { NotificationSettingsPage } from '@/features/notifications/pages/NotificationSettingsPage';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { LoadingLayout } from '@/layouts/LoadingLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SessionExpiredPage } from '@/pages/SessionExpiredPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';

import { GuestRoute } from './GuestRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleRoute } from './RoleRoute';
import { RootRoute } from './RootRoute';

// Role-agnostic — every authenticated persona reaches these through the same TopNav account menu.
const accountNavItems: NavItem[] = [
  { label: 'Profile', to: ROUTES.profile, icon: <PersonOutlineIcon fontSize="small" /> },
  {
    label: 'Change Password',
    to: ROUTES.changePassword,
    icon: <VpnKeyOutlinedIcon fontSize="small" />,
  },
  { label: 'Security (MFA)', to: ROUTES.mfaSetup, icon: <ShieldOutlinedIcon fontSize="small" /> },
  {
    label: 'Notifications',
    to: ROUTES.notificationSettings,
    icon: <NotificationsNoneOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Invitations',
    to: ROUTES.accountInvitations,
    icon: <MarkEmailReadOutlinedIcon fontSize="small" />,
  },
];

// Each persona's route table is its own lazy chunk (ADR-0004) — a Candidate user's bundle never
// includes Recruiter/Company/Vendor/Admin code.
// The onboarding/invitation screens are one-off journeys — each account sees them at most a handful
// of times — so they stay out of the main bundle too (ADR-0004).
const OnboardingPage = lazy(() =>
  import('@/features/onboarding/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const WorkspaceChooserPage = lazy(() =>
  import('@/features/onboarding/pages/WorkspaceChooserPage').then((m) => ({
    default: m.WorkspaceChooserPage,
  })),
);
const InvitationLandingPage = lazy(() =>
  import('@/features/company-portal/pages/InvitationLandingPage').then((m) => ({
    default: m.InvitationLandingPage,
  })),
);

// The public job board is its own chunk — an anonymous visitor's bundle stays free of persona code.
const JobBoardPage = lazy(() =>
  import('@/features/public-jobs/pages/JobBoardPage').then((m) => ({ default: m.JobBoardPage })),
);
const JobDetailPage = lazy(() =>
  import('@/features/public-jobs/pages/JobDetailPage').then((m) => ({ default: m.JobDetailPage })),
);

// Employer discovery (ADR-0023 §7) — the public company directory/profile/jobs tab. Its own chunk for the
// same reason the board is: an anonymous visitor researching employers pulls no persona code (ADR-0004).
const EmployerDirectoryPage = lazy(() =>
  import('@/features/employers/pages/EmployerDirectoryPage').then((m) => ({
    default: m.EmployerDirectoryPage,
  })),
);
const EmployerProfilePage = lazy(() =>
  import('@/features/employers/pages/EmployerProfilePage').then((m) => ({
    default: m.EmployerProfilePage,
  })),
);
const EmployerJobsPage = lazy(() =>
  import('@/features/employers/pages/EmployerJobsPage').then((m) => ({ default: m.EmployerJobsPage })),
);

// The legal documents (ADR-0026) — public, and their own chunk: they are read once, by a fraction of
// visitors, and pull nothing else with them.
const LegalDocumentPage = lazy(() =>
  import('@/features/legal/pages/LegalDocumentPage').then((m) => ({ default: m.LegalDocumentPage })),
);
const LegalIndexPage = lazy(() =>
  import('@/features/legal/pages/LegalIndexPage').then((m) => ({ default: m.LegalIndexPage })),
);
const LegalPolicyPage = lazy(() =>
  import('@/features/legal/pages/LegalPolicyPage').then((m) => ({ default: m.LegalPolicyPage })),
);

const CandidateRoutes = lazy(() => import('./candidateRoutes'));
const CompanyRoutes = lazy(() => import('./companyRoutes'));
const AdminRoutes = lazy(() => import('./adminRoutes'));

/** The root route composition (docs/development/FrontendGuidelines.md §2.1 `routes/index.tsx`). */
export function AppRoutes() {
  return (
    <Routes>
      {/* `/` — public (ADR-0035). OUTSIDE every auth gate, because the site root has to answer a
          visitor with no account and a crawler with no session; it previously sat inside
          `ProtectedRoute` and served them a login form. `RootRoute` still hands an AUTHENTICATED
          visitor to `RootRedirect`, so the one address keeps both behaviours and nothing about
          post-login resolution changed. */}
      <Route index element={<RootRoute />} />

      {/* Pre-authentication screens (Identity feature). */}
      <Route element={<GuestRoute />}>
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route path={ROUTES.register} element={<RegisterPage />} />
          <Route path={ROUTES.verifyEmail} element={<VerifyEmailPage />} />
          <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />
          <Route path={ROUTES.mfaChallenge} element={<MfaChallengePage />} />
          <Route path={ROUTES.forgotMfa} element={<ForgotMfaPage />} />
        </Route>
      </Route>

      {/* Company registration — public, but OUTSIDE GuestRoute: a signed-in
          applicant whose registration was rejected resubmits from here without being bounced to
          their dashboard. Deliberately separate from Candidate registration. */}
      <Route element={<AuthLayout />}>
        <Route path={ROUTES.registerCompany} element={<RegisterCompanyPage />} />
        <Route path={ROUTES.registerCompanyVerify} element={<VerifyCompanyRegistrationPage />} />
        {/* Anonymous invitation landing page (Company module Stage 2) — also outside GuestRoute:
            a signed-in user follows the same email link to accept/decline in place. */}
        <Route
          path={ROUTES.invitationAccept}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <InvitationLandingPage />
            </Suspense>
          }
        />
      </Route>

      {/* Public job board (Candidate Portal) — anonymous browsing, outside every auth gate. A
          signed-in visitor keeps their session (TopNav shows their account); a guest sees "Sign in".
          Its own lazy chunk so a visitor's first paint never pulls in persona code (ADR-0004). */}
      <Route element={<PublicLayout />}>
        <Route
          path={ROUTES.jobs}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <JobBoardPage />
            </Suspense>
          }
        />
        <Route
          path={`${ROUTES.jobs}/:jobId`}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <JobDetailPage />
            </Suspense>
          }
        />

        {/* Terms of Service and Privacy Policy (ADR-0026) — anonymous, and OUTSIDE GuestRoute: the
            registration form links here in a new tab, and a signed-in user must be able to re-read what
            they accepted without being bounced to their dashboard. */}
        <Route
          path={ROUTES.legalTerms}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <LegalDocumentPage kind="terms" />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.legalPrivacy}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <LegalDocumentPage kind="privacy" />
            </Suspense>
          }
        />
        {/* The index of everything published, and every other document by its API slug. Declared AFTER
            the two fixed routes above so `/legal/terms` and `/legal/privacy` keep matching them — React
            Router ranks a static segment above a dynamic one regardless of order, but relying on that
            silently is how the fixed routes would one day stop resolving. */}
        <Route
          path={ROUTES.legalIndex}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <LegalIndexPage />
            </Suspense>
          }
        />
        <Route
          path={`${ROUTES.legalIndex}/:slug`}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <LegalPolicyPage />
            </Suspense>
          }
        />

        {/* Employer discovery (ADR-0023 §7) — anonymous, and a PEER of the job board above rather than a
            branch of it. `/companies/:slug` is the canonical shareable employer address and the target of
            invitation notifications, so it must resolve without an account. The nested jobs tab reuses the
            board's own job detail page, so a posting opened from an employer keeps that context in the URL
            instead of jumping to `/jobs/:jobId`. */}
        <Route
          path={ROUTES.companies}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <EmployerDirectoryPage />
            </Suspense>
          }
        />
        <Route
          path={`${ROUTES.companies}/:slug`}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <EmployerProfilePage />
            </Suspense>
          }
        />
        <Route
          path={`${ROUTES.companies}/:slug/jobs`}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <EmployerJobsPage />
            </Suspense>
          }
        />
        <Route
          path={`${ROUTES.companies}/:slug/jobs/:jobId`}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <JobDetailPage />
            </Suspense>
          }
        />
      </Route>

      {/* Standing status pages — never a blank screen for these conditions (§6.3, §7.10). */}
      <Route path={ROUTES.sessionExpired} element={<SessionExpiredPage />} />
      <Route path={ROUTES.unauthorized} element={<UnauthorizedPage />} />
      <Route path={ROUTES.forbidden} element={<ForbiddenPage />} />

      {/* Authenticated app — role-gated per ADR-0004. */}
      <Route element={<ProtectedRoute />}>
        {/* No `index` route here any more — `/` moved above this gate (ADR-0035). The authenticated
            resolution it used to hold still runs, from inside `RootRoute`. */}

        {/* Context-aware onboarding (Company module Stage 2): the neutral first-journey chooser
            and the dual-persona workspace chooser. Both self-forward accounts that don't belong
            on them, so deep links never strand anyone. */}
        <Route
          path={ROUTES.welcome}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <OnboardingPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.workspaces}
          element={
            <Suspense fallback={<LoadingLayout />}>
              <WorkspaceChooserPage />
            </Suspense>
          }
        />

        {/* Role-agnostic account/security screens (Identity feature). The banner gives
            every account page a consistent "← Back to Dashboard" return path (Task 1), resolved to
            the signed-in persona's own dashboard. */}
        <Route
          path="/account"
          element={
            <DashboardLayout
              navItems={accountNavItems}
              banner={
                <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
                  <BackToDashboardLink />
                </Box>
              }
            />
          }
        >
          <Route path="profile" element={<ProfilePage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="mfa-setup" element={<MfaSetupPage />} />
          <Route path="notifications" element={<NotificationSettingsPage />} />
          <Route path="notifications/all" element={<NotificationHistoryPage />} />
          <Route path="invitations" element={<MyInvitationsPage />} />
        </Route>

        <Route element={<RoleRoute roles={[ROLES.Candidate]} />}>
          <Route
            path={`${ROUTES.candidate}/*`}
            element={
              <Suspense fallback={<LoadingLayout />}>
                <CandidateRoutes />
              </Suspense>
            }
          />
        </Route>

        {/* The Company Portal is MEMBERSHIP-gated, not role-gated (Stage 1 decision): an invited
            member holds no platform role at all, and a pending founder must reach their
            registration status screens. */}
        <Route element={<CompanyGuard />}>
          <Route
            path={`${ROUTES.company}/*`}
            element={
              <Suspense fallback={<LoadingLayout />}>
                <CompanyRoutes />
              </Suspense>
            }
          />
        </Route>

        {/* No /recruiter and no /vendor. Both were shells of ComingSoonPage placeholders. Recruiting
            happens in the company workspace above, entered through membership; Vendor is unbuilt.
            The seeded platform roles stay (RBAC v1 is frozen) — but no flow grants either, so
            nothing became unreachable by deleting the shells. */}

        {/* All three platform tiers reach the console (ADR-0032). What each one SEES inside it is
            decided by `AdminRoutes`, which filters the nav and gates the pages per tier — gating only
            here would have let Support open the candidate directory. */}
        <Route element={<RoleRoute roles={PLATFORM_TIERS} />}>
          <Route
            path={`${ROUTES.admin}/*`}
            element={
              <Suspense fallback={<LoadingLayout />}>
                <AdminRoutes />
              </Suspense>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
